// src/autodesk_usage/pages/AutodeskCustomerDetailPage.tsx
// All comments in English only
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomerDetail } from "../hooks/useCustomerDetail";
import CustomerSummaryCard from "../components/customerDetail/CustomerSummaryCard";
import CustomerLLMSummaryPanel from "../components/customerDetail/CustomerLLMSummaryPanel";
import RiskHistoryChart from "../components/customerDetail/RiskHistoryChart";
import ProductRiskTrendChart from "../components/customerDetail/ProductRiskTrendChart";
import ProductLLMPanel from "../components/customerDetail/ProductLLMPanel";
import ProductTrendCard from "../components/customerDetail/ProductTrendCard";
import Spinner from "@/components/shared/Spinner";
import ErrorState from "@/components/shared/ErrorState";

export default function AutodeskCustomerDetailPage() {
  const { company_name } = useParams();
  const navigate = useNavigate();
  const company = company_name || "";

  const { summary, riskHistory, perProduct, loading, error } = useCustomerDetail(
    company,
    { months: 12 }
  );

  const title = useMemo(
    () => summary?.company_name || company,
    [summary, company]
  );

  useEffect(() => {
    if ((error as any)?.status === 403) {
      navigate("/autodesk-usage/corporate", { replace: true });
    }
  }, [error, navigate]);

  if (loading)
    return (
      <div className="p-6">
        <Spinner />
      </div>
    );

  if (error)
    return (
      <div className="p-6">
        <ErrorState message={(error as any)?.message || "Failed to load"} />
      </div>
    );

  if (!summary)
    return (
      <div className="p-6">
        <ErrorState message="No data" />
      </div>
    );

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* --- Customer Summary --- */}
      <CustomerSummaryCard
        company={title}
        asOf={summary.as_of_month}
        riskScore={summary.risk_score_final}
        riskLevel={summary.risk_level}
        churnProb={summary.churn_probability_company}
        renewalDue={summary.renewal_due_ratio}
        renewed={summary.renewed_ratio}
        expired={summary.expired_ratio}
        topProducts={summary.top_risk_products}
      />

      {/* --- Company-level LLM Summary --- */}
      <CustomerLLMSummaryPanel
        summary={summary.llm_summary ?? summary.risk_label_primary}
        keyFactors={summary.llm_key_factors}
        actions={summary.llm_actions}
      />

      {/* --- Company Risk History --- */}
      {riskHistory?.series?.length ? (
        <RiskHistoryChart data={riskHistory.series} />
      ) : null}

      {/* --- Per-product section --- */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">
            Product-level usage, risk and AI insights
          </h2>
          <p className="text-sm text-muted-foreground">
            Each product section shows monthly used users vs active seats, risk
            metrics trend and AI-generated analysis for decision support.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8">
          {perProduct.map(({ meta, trend, riskTrend, llm }) => {
            const llmPromptRaw =
              llm &&
              llm.llm_prompt &&
              typeof llm.llm_prompt.raw === "string"
                ? llm.llm_prompt.raw
                : undefined;

            const hasTrend = !!trend?.series?.length;
            const hasRiskTrend = !!riskTrend?.series?.length;

            return (
              <div
                key={meta.product}
                className="rounded-2xl bg-background p-4 md:p-5 space-y-6 shadow-sm"
              >
                {/* Product title */}
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base md:text-lg font-semibold text-foreground">
                    {meta.product}
                  </h3>
                  {meta.as_of_month && (
                    <span className="text-xs md:text-sm text-muted-foreground">
                      As of {meta.as_of_month}
                    </span>
                  )}
                </div>

                {/* 1️⃣ Used Users vs Active Seats */}
                {hasTrend && (
                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2.2fr)_minmax(0,1.4fr)] gap-4 items-stretch">
                    <div className="h-full">
                      <ProductTrendCard
                        product={""}
                        series={trend.series as any}
                      />
                    </div>
                    <div className="bg-gradient-to-br from-primary/5 via-muted/40 to-background border border-primary/20 rounded-xl p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="inline-flex items-center gap-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                          How to read
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Capacity vs. actual usage
                        </span>
                      </div>
                      <p className="text-sm text-foreground/80 leading-relaxed">
                        This chart compares the number of active users each
                        month against the number of available seats. It helps
                        you understand whether the customer is overusing or
                        underusing their licenses.
                      </p>
                      <ul className="text-sm text-foreground/80 leading-relaxed list-disc list-inside space-y-1">
                        <li>
                          <span className="font-medium text-foreground">
                            Overuse:
                          </span>{" "}
                          usage exceeds license count, indicating compliance
                          risk and a strong upsell opportunity.
                        </li>
                        <li>
                          <span className="font-medium text-foreground">
                            Underuse:
                          </span>{" "}
                          low usage compared to capacity, suggesting potential
                          downsell or right-sizing at renewal.
                        </li>
                        <li>
                          <span className="font-medium text-foreground">
                            Trend:
                          </span>{" "}
                          a sustained upward trend points to growing adoption,
                          while a downward trend may flag churn risk.
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* 2️⃣ Risk Metrics Trend */}
                {hasRiskTrend && (
                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2.2fr)_minmax(0,1.4fr)] gap-4 items-stretch">
                    <div className="h-full">
                      <ProductRiskTrendChart
                        product={""}
                        series={riskTrend.series as any}
                      />
                    </div>
                    <div className="bg-gradient-to-br from-amber-50/70 via-muted/40 to-background border border-amber-200 rounded-xl p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="inline-flex items-center gap-2">
                        <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          How to read
                        </span>
                        <span className="text-xs text-amber-700/80">
                          Risk evolution over time
                        </span>
                      </div>
                      <p className="text-sm text-slate-800 leading-relaxed">
                        This visualization shows how utilization-based metrics
                        and the overall risk score evolve over time, so you can
                        spot deteriorating or improving conditions early.
                      </p>
                      <ul className="text-sm text-slate-800 leading-relaxed list-disc list-inside space-y-1">
                        <li>
                          <span className="font-medium text-emerald-600">
                            High utilization:
                          </span>{" "}
                          values close to 100% signal pressure on capacity and
                          higher overuse risk.
                        </li>
                        <li>
                          <span className="font-medium text-cyan-600">
                            EWMA line:
                          </span>{" "}
                          smoothed utilization that highlights the underlying
                          trend instead of one-off spikes.
                        </li>
                        <li>
                          <span className="font-medium text-orange-600">
                            Risk score:
                          </span>{" "}
                          aggregates multiple dimensions into one index; rising
                          scores indicate growing risk, stable or falling scores
                          point to more controlled situations.
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* 3️⃣ LLM Analysis + explanation below the internal title */}
                <div className="space-y-3">
                  {/* Original panel (keeps its own “— LLM Analysis” title) */}
                  <ProductLLMPanel
                    product={""}
                    analysis={llm?.analysis_text}
                    actionsText={llm?.actions_text}
                    riskSummary={llm?.risk_reason_summary ?? null}
                    explainCodes={llm?.explain_codes ?? null}
                    llmModelName={llm?.llm_model_name ?? null}
                    llmGeneratedAt={llm?.llm_generated_at ?? null}
                    dataVersion={llm?.data_version ?? null}
                    llmPromptRaw={llmPromptRaw}
                    llmRawOutput={llm?.llm_raw_output}
                  />

                  {/* Collapsible explanation, visually attached to the LLM section */}
                  <details className="rounded-xl border border-primary/15 bg-muted/40 p-4 text-sm text-muted-foreground shadow-sm">
                    <summary className="cursor-pointer font-medium text-foreground">
                      How to interpret this AI summary
                    </summary>
                    <div className="mt-3 space-y-2 leading-relaxed">
                      <p className="text-foreground/80">
                        This AI-generated summary explains what the recent
                        usage, capacity and risk metrics mean for this
                        product&apos;s renewal health and growth potential.
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-foreground/80">
                        <li>
                          <span className="font-medium text-foreground">
                            Summary:
                          </span>{" "}
                          describes the current risk level and churn likelihood
                          using the latest data window.
                        </li>
                        <li>
                          <span className="font-medium text-foreground">
                            Key drivers:
                          </span>{" "}
                          highlights the main reasons behind the risk, such as
                          sustained overuse, sharp drops in adoption or highly
                          volatile utilization.
                        </li>
                        <li>
                          <span className="font-medium text-foreground">
                            Recommended actions:
                          </span>{" "}
                          suggests practical next steps for account teams, like
                          upsell conversations, right-sizing proposals or
                          enablement programs.
                        </li>
                      </ul>
                      <p className="text-foreground/80">
                        Use this section as a quick briefing before customer
                        calls or QBRs, then cross-check the details in the
                        charts above when you need additional context.
                      </p>
                    </div>
                  </details>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
